import React from 'react';

import {
  AccordionContainer,
  AccordionHeader,
  AccordionHeaderLeft,
  ChevronIcon,
  AccordionContentWrapper,
  AccordionContentInner,
} from './Accordion.styles';
import { Heading, Text } from '@amzn/storm-ui';

export interface AccordionProps {
  /** Unique identifier for the accordion */
  id: string;
  /** Main title displayed in the accordion header */
  title: string;
  /** Whether the accordion is currently expanded */
  isExpanded: boolean;
  /** Callback fired when the accordion header is clicked to toggle expand/collapse */
  onToggle: () => void;
  /** The content to display inside the accordion body */
  children: React.ReactNode;
  /** Optional content to render on the right side of the header (e.g., action buttons) */
  headerActions?: React.ReactNode;
  /** Optional subtitle displayed next to the title */
  subtitle?: string;
}

export const Accordion: React.FC<AccordionProps> = ({
  id,
  title,
  subtitle,
  isExpanded,
  onToggle,
  headerActions,
  children,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <AccordionContainer id={id}>
      <AccordionHeader
        onClick={onToggle}
        aria-expanded={isExpanded}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <AccordionHeaderLeft>
          <ChevronIcon
            type="chevron-down"
            size="small"
            isExpanded={isExpanded}
          />
          <Heading renderAs="h6">{title}</Heading>
          {subtitle && <Text>— {subtitle}</Text>}
        </AccordionHeaderLeft>
        {headerActions && (
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {headerActions}
          </div>
        )}
      </AccordionHeader>
      <AccordionContentWrapper isExpanded={isExpanded}>
        <AccordionContentInner isExpanded={isExpanded}>
          {children}
        </AccordionContentInner>
      </AccordionContentWrapper>
    </AccordionContainer>
  );
};
